import type { Control, ControllerRenderProps } from "react-hook-form";

import type { ModifierFormSchema } from "../../../lib/validators/modifiers";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../../ui/form";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  InputGroupText,
} from "../../ui/input-group";

/**
 * Formats a raw price input string, enforcing max 3 integer digits and 2 decimal digits.
 */
function formatPriceInput(rawValue: string): string {
  const cleanedValue = rawValue.replace(/[^\d.]/g, "");
  const parts = cleanedValue.split(".");

  let integerPart = parts[0] ?? "";
  if (integerPart.length > 3) {
    integerPart = integerPart.slice(0, 3);
  }

  let formattedValue = integerPart;
  if (parts.length > 1) {
    let decimalPart = parts[1] ?? "";
    if (decimalPart.length > 2) {
      decimalPart = decimalPart.slice(0, 2);
    }
    formattedValue = `${integerPart}.${decimalPart}`;
  }

  return formattedValue;
}

/**
 * Formats a price value on blur to ensure "X.XX" format.
 */
function formatPriceOnBlur(currentValue: string | undefined): string {
  if (!currentValue || currentValue === "") {
    return "0.00";
  }

  const parts = currentValue.split(".");
  const integerPart = parts[0] ?? "0";
  const decimalPart = (parts[1] ?? "00").padEnd(2, "0").slice(0, 2);

  return `${integerPart}.${decimalPart}`;
}

function PriceInput({
  field,
}: {
  field: {
    name: string;
    value: string;
    onChange: (value: string) => void;
    onBlur: () => void;
  };
}) {
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    field.onChange(formatPriceInput(e.target.value));
  };

  const handleBlur = () => {
    field.onChange(formatPriceOnBlur(field.value));
    field.onBlur();
  };

  return (
    <InputGroup className="h-9 rounded-md">
      <InputGroupAddon align="inline-start">
        <InputGroupText>$</InputGroupText>
      </InputGroupAddon>
      <InputGroupInput
        className="body-sm placeholder:body-sm px-3 py-2"
        inputMode="decimal"
        name={field.name}
        onBlur={handleBlur}
        onChange={handleChange}
        placeholder="0.00"
        value={field.value}
      />
    </InputGroup>
  );
}

export const PriceField = ({
  control,
  index,
}: {
  control: Control<ModifierFormSchema>;
  index: number;
}) => {
  return (
    <FormField
      control={control}
      name={`modifiersList.${index}.data.additionalCost`}
      render={({ field }) => (
        <FormItem>
          <FormLabel>Price</FormLabel>
          <FormControl>
            <PriceInput field={field} />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
};

export const PriceFieldSub = ({
  field,
}: {
  field: ControllerRenderProps<
    ModifierFormSchema,
    `modifiersList.${number}.data.modifiers.${number}.additionalCost`
  >;
}) => {
  return <PriceInput field={field} />;
};
